// Item tooltip data — the one item tooltip every surface shares (inventory,
// shop, trade, crafting, action bar, NPC tome rewards and item chips).
// Split from Tooltip.tsx (lane BI) when the compare-to-worn delta arrived.

import { formatCallingWithArchetype } from '../theme';
import { ITEM_DATABASE } from '../../../shared/ItemData';
import { CONSUMABLE_DATABASE } from '../../../shared/content/generated/consumables.gen';
import { ABILITY_EFFECT_DATABASE } from '../../../shared/content/generated/ability-effects.gen';
import { ITEM_SLOT_LABELS } from '../../../shared/equipmentSlots';
import { boundItemLabel } from '../../../shared/inventory/itemBinding';
import type { ItemComparison, ItemTooltipViewerContext, TooltipData, TooltipRequirement } from './Tooltip';
import { isTouchDevice } from '../mobile/touchControlsFlag';

/**
 * The tooltip's "against what you wear" block (plan 23 row 27). Undefined
 * when the item has no slot or the caller does not know what is worn; an
 * empty slot compares against nothing, so every stat reads as a gain.
 */
export function compareWithWorn(
  itemId: string | undefined,
  slot: string | undefined,
  stats: Record<string, number>,
  viewer: ItemTooltipViewerContext | undefined,
): ItemComparison | undefined {
  if (!slot || !viewer?.equipped) return undefined;
  const worn = viewer.equipped[slot] ?? null;
  const wornName = worn ? (ITEM_DATABASE[worn.id]?.name ?? worn.name ?? worn.id) : null;
  if (worn && itemId && worn.id === itemId) return { against: wornName, worn: true, deltas: [] };
  const wornStats = !worn ? {}
    : worn.stats && Object.keys(worn.stats).length > 0 ? worn.stats : numericOnly(ITEM_DATABASE[worn.id]?.stats);
  // Keyed by label, so `magic_attack` and `magicAttack` are one stat.
  const byLabel = new Map<string, number>();
  const add = (source: Record<string, number>, sign: number) => {
    for (const [key, value] of Object.entries(source)) {
      const label = ITEM_STAT_LABELS[key] ?? humanizeId(key);
      byLabel.set(label, (byLabel.get(label) ?? 0) + sign * value);
    }
  };
  add(stats, 1);
  add(wornStats, -1);
  const deltas = [...byLabel].map(([label, delta]) => ({ label, delta }))
    .filter(entry => Number.isFinite(entry.delta) && entry.delta !== 0);
  return { against: wornName, worn: false, deltas };
}

// Helper to create tooltip data from an item
export function createItemTooltip(item: {
  id?: string;
  name: string;
  description?: string;
  rarity?: 'common' | 'uncommon' | 'rare' | 'epic' | 'legendary' | 'unique';
  type?: string;
  stats?: Record<string, number>;
  quantity?: number;
}, viewer?: ItemTooltipViewerContext, options: { actions?: 'bag' } = {}): TooltipData {
  const canonical = item.id ? ITEM_DATABASE[item.id] : undefined;
  const consumable = item.id ? CONSUMABLE_DATABASE[item.id] : undefined;
  const type = canonical?.type ?? item.type;
  const sourceStats = item.stats && Object.keys(item.stats).length > 0
    ? item.stats
    : canonical?.stats;
  const stats = sourceStats && type !== 'consumable'
    ? Object.entries(sourceStats)
      .filter((entry): entry is [string, number] => typeof entry[1] === 'number')
      .map(([key, value]) => ({
        label: ITEM_STAT_LABELS[key] ?? humanizeId(key),
        value,
      }))
    : undefined;
  const effects = consumable?.effects.flatMap((effectId: string) => {
    const effect = ABILITY_EFFECT_DATABASE[effectId];
    return effect ? [describeConsumableEffect(effect)] : [];
  });
  const requiredLevel = Math.max(canonical?.requiredLevel ?? 1, consumable?.levelRequirement ?? 1);
  const requirements: TooltipRequirement[] = [];
  if (requiredLevel > 1) {
    const hasViewerLevel = typeof viewer?.level === 'number' && Number.isFinite(viewer.level);
    requirements.push({
      text: `Requires Level ${requiredLevel}`,
      status: !hasViewerLevel ? 'unknown' : viewer.level! >= requiredLevel ? 'met' : 'unmet',
    });
  }
  if (canonical?.requiredClass) {
    const viewerClass = viewer?.characterClass;
    requirements.push({
      text: `Requires ${formatCallingWithArchetype(canonical.requiredClass)} class`,
      status: viewerClass == null
        ? 'unknown'
        : viewerClass === canonical.requiredClass
          ? 'met'
          : 'unmet',
    });
  }
  const rawRarity = canonical?.rarity ?? item.rarity;
  const rarity = rawRarity === 'unique' ? 'legendary' : rawRarity;

  const slot = (type === 'weapon' || type === 'armor') ? canonical?.equipmentSlot : undefined;
  const numericStats = type !== 'consumable' ? numericOnly(sourceStats) : {};
  return {
    title: canonical?.name ?? item.name,
    subtitle: slot ? ITEM_SLOT_LABELS[slot] : undefined,
    binding: boundItemLabel(canonical?.binding) ?? undefined,
    comparison: compareWithWorn(item.id, slot, numericStats, viewer),
    description: canonical?.description ?? item.description,
    rarity,
    stats,
    effects,
    requirements,
    extraInfo: item.quantity && item.quantity > 1 ? `Quantity: ${item.quantity}` : undefined,
    // Only an item in the bag can be used, equipped or dragged; a preview offers no action.
    interactionHint: options.actions === 'bag' ? bagHint(type) : undefined,
  };
}

/** What a bag item does from here: a touch has no right-click, and selects the item to act on it. */
function bagHint(type: string | undefined): string | undefined {
  const wearable = type === 'weapon' || type === 'armor';
  if (isTouchDevice()) return type === 'consumable' ? 'Tap, then Use' : wearable ? 'Tap, then Equip' : undefined;
  if (type === 'consumable') return 'Right-click to use • Drag to the action bar';
  return wearable ? 'Right-click to equip • Drag to the character screen or matching slot' : 'Drag item';
}

const ITEM_STAT_LABELS: Record<string, string> = {
  hp: 'Maximum Health',
  mp: 'Maximum Mana',
  attack: 'Attack',
  magicAttack: 'Magic Attack',
  magic_attack: 'Magic Attack',
  defense: 'Defense',
  strength: 'Strength',
  dexterity: 'Dexterity',
  intelligence: 'Intelligence',
  luck: 'Luck',
  speed: 'Movement Speed',
  criticalDamage: 'Critical Damage',
  critical_damage: 'Critical Damage',
};

function numericOnly(stats: Record<string, unknown> | undefined): Record<string, number> {
  return Object.fromEntries(Object.entries(stats ?? {}).filter((entry): entry is [string, number] => typeof entry[1] === 'number'));
}

function humanizeId(value: string): string {
  return value
    .replace(/([a-z])([A-Z])/g, '$1 $2')
    .replace(/[_-]+/g, ' ')
    .replace(/\b\w/g, letter => letter.toUpperCase());
}

function formatDuration(seconds: number): string {
  if (seconds >= 60 && seconds % 60 === 0) {
    const minutes = seconds / 60;
    return `${minutes} minute${minutes === 1 ? '' : 's'}`;
  }
  return `${seconds} second${seconds === 1 ? '' : 's'}`;
}

function joinList(values: string[]): string {
  if (values.length <= 1) return values[0] ?? '';
  if (values.length === 2) return `${values[0]} and ${values[1]}`;
  return `${values.slice(0, -1).join(', ')}, and ${values[values.length - 1]}`;
}

function describeConsumableEffect(
  effect: (typeof ABILITY_EFFECT_DATABASE)[string],
): string {
  const resource = effect.restoreResource === 'mana' ? 'Mana' : 'Health';
  switch (effect.effectType) {
  case 'heal':
    return effect.baseValue >= 99999
      ? `Fully restores ${resource}.`
      : `Restores ${effect.baseValue} ${resource}.`;
  case 'hot': {
    const tickCount = effect.tickIntervalSec > 0
      ? Math.max(1, Math.floor(effect.durationSec / effect.tickIntervalSec))
      : 1;
    const perTick = Math.max(1, Math.round(effect.baseValue / tickCount));
    const cadence = effect.tickIntervalSec > 0
      ? ` (${perTick} every ${formatDuration(effect.tickIntervalSec)})`
      : '';
    return `Restores ${effect.baseValue} ${resource} over ${formatDuration(effect.durationSec)}${cadence}.`;
  }
  case 'buff': {
    const bonuses = Object.entries(effect.statBonuses ?? {})
      .map(([stat, value]) => `+${value} ${ITEM_STAT_LABELS[stat] ?? humanizeId(stat)}`);
    const duration = effect.durationSec > 0 ? ` for ${formatDuration(effect.durationSec)}` : '';
    if (bonuses.length > 0) return `Grants ${joinList(bonuses)}${duration}.`;
    return `Applies ${effect.name}${duration}.`;
  }
  case 'dispel': {
    const tags = (effect.tags ?? []).map(humanizeId);
    return tags.length > 0
      ? `Removes ${joinList(tags)} effects.`
      : 'Removes harmful effects.';
  }
  default:
    return effect.durationSec > 0
      ? `Applies ${effect.name} for ${formatDuration(effect.durationSec)}.`
      : `Applies ${effect.name}.`;
  }
}
